import { ThunderboltFilled } from '@ant-design/icons';
import { Divider, Form, Modal } from 'antd';
import { Fragment, useCallback, useEffect, useMemo, useState } from 'react';

import { DoubleThunderbolt } from 'app/components/commons/DoubleThunderbolt/DoubleThunderbolt';
import { HowDoesItWork } from 'app/components/commons/HowDoesItWork/HowDoesItWork';
import {
  useAutomateDiscountDelete,
  useAutomateDiscountSave,
} from 'app/hooks/data/useAutomateDiscount';
import type { ExtranetPackage } from 'app/typings/packages';
import { isValidDiscount } from 'app/utils/price';
import { amplitudeTrack } from 'app/utils/track/amplitude';
import { assertNever } from 'app/utils/typing';

import '../PricingRule.scss';
import { RuleSection } from '../RuleSection';
import type { PricingRuleFormValues, RuleKeys, RuleType } from '../Utils';
import { getInitialFormValues } from '../Utils';

import './PricingRuleModal.scss';
import { PricingRuleModalFooter } from './PricingRuleModalFooter';
import { PricingRuleModalHeader } from './PricingRuleModalHeader';

export type FieldActiveRules = {
  pkgId: number;
  standardDiscount: boolean;
  lastMinuteDiscount: boolean;
}[];

type Props = {
  onClose: () => void;
  isOpen: boolean;
  onCompleteTask?: () => void;
  hotelId: number;
  packages: ExtranetPackage[];
  isSingleRate: boolean;
};

export const PricingRuleModal = ({
  onClose,
  isOpen,
  onCompleteTask,
  hotelId,
  isSingleRate,
  packages,
}: Props) => {
  const [form] = Form.useForm<PricingRuleFormValues>();
  const formValues = Form.useWatch([], form);
  const [canSubmit, setCanSubmit] = useState<boolean>(false);

  const initialDisplayValues = useMemo(
    () =>
      packages.map((pkg) => ({
        pkgId: pkg.id,
        standardDiscount: !!pkg.autoDiscountRate,
        lastMinuteDiscount:
          !!pkg.lastMinuteAutoDiscountRate || !!pkg.lastMinuteAutoDiscountDays,
      })),
    [packages]
  );

  useEffect(() => {
    const initialFormValues = getInitialFormValues(packages);

    packages.forEach((pkg, idx) => {
      form.setFieldValue(['packageRules', idx, 'packageId'], pkg.id);
    });
    form.setFieldsValue(initialFormValues);
  }, [packages, form]);

  const [activeRules, setActiveRules] =
    useState<FieldActiveRules>(initialDisplayValues);

  const { mutateAsync: updatePricingRule, isLoading: updatingPricingRule } =
    useAutomateDiscountSave(hotelId);

  const { mutateAsync: deletePricingRule, isLoading: deletingPricingRule } =
    useAutomateDiscountDelete(hotelId);

  const handleSaveForm = async (values: PricingRuleFormValues) => {
    const formItems = packages.map((pkg) => {
      const isActiveDiscount = !!activeRules.find(
        (rule) => rule.pkgId === pkg.id
      )?.standardDiscount;
      const isActiveLMDiscount = !!activeRules.find(
        (rule) => rule.pkgId === pkg.id
      )?.lastMinuteDiscount;

      const pkgIdx = values.packageRules.findIndex(
        (pr) => pr.packageId === pkg.id
      );

      const discountValue = values.packageRules[pkgIdx].discount;
      const lmDiscountValue =
        values.packageRules[pkgIdx].lastMinuteDiscountValue;
      const lmDiscountDays = values.packageRules[pkgIdx].lastMinuteDays;

      return {
        packageId: pkg.id,
        discount:
          isActiveDiscount && discountValue ? discountValue / 100 : undefined,
        lastMinuteDiscount:
          isActiveLMDiscount &&
          lmDiscountValue !== undefined &&
          lmDiscountDays !== undefined
            ? {
                discount: lmDiscountValue / 100,
                days: lmDiscountDays,
              }
            : undefined,
      };
    });

    await updatePricingRule({
      packageAutomations: formItems,
    });

    packages.forEach((pkg) => {
      const pkgIdx = activeRules.findIndex((ar) => ar.pkgId === pkg.id);

      if (
        !initialDisplayValues[pkgIdx].standardDiscount &&
        activeRules[pkgIdx].standardDiscount
      ) {
        amplitudeTrack('Activate default pricing rule', {
          min_discount_value: values.packageRules[pkgIdx].discount + '%',
          package_id: pkg.id,
        });
      } else if (
        initialDisplayValues[pkgIdx].standardDiscount &&
        !activeRules[pkgIdx].standardDiscount
      ) {
        amplitudeTrack('Deactivate default rule', { package_id: pkg.id });
      }

      if (
        !initialDisplayValues[pkgIdx].lastMinuteDiscount &&
        activeRules[pkgIdx].lastMinuteDiscount
      ) {
        amplitudeTrack('Activate last minute pricing rule', {
          last_minute_discount:
            values.packageRules[pkgIdx].lastMinuteDiscountValue + '%',
          last_minute_days: values.packageRules[pkgIdx].lastMinuteDays,
          package_id: pkg.id,
        });
      } else if (
        initialDisplayValues[pkgIdx].lastMinuteDiscount &&
        !activeRules[pkgIdx].lastMinuteDiscount
      ) {
        amplitudeTrack('Deactivation last minute pricing rule', {
          package_id: pkg.id,
        });
      }
    });

    onCompleteTask?.();
    onClose();
  };

  const handleDeactivationAutomation = async () => {
    if (!!initialDisplayValues.find((values) => values.standardDiscount)) {
      amplitudeTrack('Deactivate default rule');
    }

    if (!!initialDisplayValues.find((values) => values.lastMinuteDiscount)) {
      amplitudeTrack('Deactivation last minute pricing rule');
    }

    await deletePricingRule(hotelId);

    onClose();
  };

  const handleCancelModal = () => {
    amplitudeTrack('Click cancel button');
    form.resetFields();
    setActiveRules(initialDisplayValues);
    onClose();
  };

  const toggleDefaultSwitch = (switchValue: boolean, pkgId: number) => {
    const newValues = activeRules.map((pkgSection) => {
      if (pkgSection.pkgId === pkgId) {
        return { ...pkgSection, standardDiscount: switchValue };
      }

      return pkgSection;
    });

    const pkgIdx = formValues.packageRules.findIndex(
      (pr) => pr.packageId === pkgId
    );

    setActiveRules(newValues);

    !switchValue &&
      form.setFieldValue(['packageRules', pkgIdx, 'discount'], undefined);

    form.validateFields();
  };

  const toggleLMSwitch = (switchValue: boolean, pkgId: number) => {
    const newValues = activeRules.map((pkgSection) => {
      if (pkgSection.pkgId === pkgId) {
        return { ...pkgSection, lastMinuteDiscount: switchValue };
      }

      return pkgSection;
    });

    const pkgIdx = formValues.packageRules.findIndex(
      (pr) => pr.packageId === pkgId
    );

    setActiveRules(newValues);

    !switchValue &&
      form.setFieldValue(['packageRules', pkgIdx, 'lastMinuteDays'], undefined);

    !switchValue &&
      form.setFieldValue(
        ['packageRules', pkgIdx, 'lastMinuteDiscountValue'],
        undefined
      );

    form.validateFields();
  };

  const invalidDiscountRate = useCallback(
    (pkgId: number) => {
      if (!formValues || !formValues.packageRules) {
        return false;
      }

      const isDayPackage = packages.find((p) => p.id === pkgId)?.dayPackage;

      const isActive = activeRules.find(
        (s) => s.pkgId === pkgId
      )?.standardDiscount;

      const pkgIndex = formValues.packageRules.findIndex(
        (pr) => pr.packageId === pkgId
      );

      const discount = formValues?.packageRules[pkgIndex]?.discount;
      const cond =
        isActive &&
        (discount === null ||
          discount === undefined ||
          !isValidDiscount(-discount, !!isDayPackage));

      return !!cond;
    },
    [activeRules, formValues, packages]
  );

  const invalidLMDays = useCallback(
    (pkgId: number) => {
      if (!formValues || !formValues.packageRules) {
        return false;
      }

      const isActive = activeRules.find(
        (s) => s.pkgId === pkgId
      )?.lastMinuteDiscount;

      const pkgIndex = formValues.packageRules.findIndex(
        (pr) => pr.packageId === pkgId
      );

      const value = formValues.packageRules[pkgIndex]?.lastMinuteDays;

      return !!(
        isActive &&
        (value === null || value === undefined || value === 0)
      );
    },
    [activeRules, formValues]
  );

  const invalidLMRate = useCallback(
    (pkgId: number) => {
      if (!formValues || !formValues.packageRules) {
        return false;
      }

      const isDayPackage = packages.find((p) => p.id === pkgId)?.dayPackage;

      const isActive = activeRules.find(
        (s) => s.pkgId === pkgId
      )?.lastMinuteDiscount;

      const pkgIndex = formValues.packageRules.findIndex(
        (pr) => pr.packageId === pkgId
      );

      const value = formValues?.packageRules[pkgIndex]?.lastMinuteDiscountValue;

      return !!(
        isActive &&
        (value === null ||
          value === undefined ||
          !isValidDiscount(-value, !!isDayPackage))
      );
    },
    [activeRules, formValues, packages]
  );

  const canSubmitAutoDiscountRate = useCallback(
    (pkg: ExtranetPackage, fValues: PricingRuleFormValues): boolean => {
      const pkgIndex = fValues.packageRules.findIndex(
        (pr) => pr.packageId === pkg.id
      );

      const autoDiscountRate = fValues?.packageRules[pkgIndex]?.discount;

      return (
        !invalidDiscountRate(pkg.id) &&
        !!autoDiscountRate &&
        Math.trunc(autoDiscountRate * 100) !== pkg.autoDiscountRate
      );
    },
    [invalidDiscountRate]
  );

  const canSubmitLastMinuteAutoDiscountRate = useCallback(
    (pkg: ExtranetPackage, fValues: PricingRuleFormValues): boolean => {
      const pkgIndex = fValues.packageRules.findIndex(
        (pr) => pr.packageId === pkg.id
      );

      const value = fValues?.packageRules[pkgIndex]?.lastMinuteDiscountValue;

      return (
        !!value &&
        !invalidLMRate &&
        Math.trunc(value * 100) !== pkg.lastMinuteAutoDiscountRate
      );
    },
    [invalidLMRate]
  );

  const canSubmitLastMinuteAutoDiscountDays = useCallback(
    (pkg: ExtranetPackage, fValues: PricingRuleFormValues): boolean => {
      const pkgIndex = fValues.packageRules.findIndex(
        (pr) => pr.packageId === pkg.id
      );

      const value = fValues?.packageRules[pkgIndex]?.lastMinuteDays;

      return (
        !!value && !invalidLMDays && value !== pkg.lastMinuteAutoDiscountDays
      );
    },
    [invalidLMDays]
  );

  useEffect(() => {
    const hasFormErrors = form
      .getFieldsError()
      .some(({ errors }) => errors.length > 0);

    // eslint-disable-next-line @typescript-eslint/no-shadow -- inner-scope reuse, not a bug
    const canSubmit =
      formValues &&
      formValues.packageRules &&
      !!packages
        .map(
          (pkg) =>
            canSubmitAutoDiscountRate(pkg, formValues) ||
            canSubmitLastMinuteAutoDiscountRate(pkg, formValues) ||
            canSubmitLastMinuteAutoDiscountDays(pkg, formValues)
        )
        .includes(true);

    setCanSubmit(canSubmit || !hasFormErrors);
  }, [
    canSubmitAutoDiscountRate,
    canSubmitLastMinuteAutoDiscountDays,
    canSubmitLastMinuteAutoDiscountRate,
    form,
    formValues,
    packages,
  ]);

  useEffect(() => {
    setActiveRules(
      packages.map((pkg) => ({
        pkgId: pkg.id,
        standardDiscount: !!pkg.autoDiscountRate,
        lastMinuteDiscount:
          !!pkg.lastMinuteAutoDiscountRate || !!pkg.lastMinuteAutoDiscountDays,
      }))
    );
  }, [packages]);

  const getIsRuleInvalid = (pkgId: number, rule: RuleKeys) => {
    switch (rule) {
      case 'standardDiscount':
        return invalidDiscountRate(pkgId);

      case 'lastMinuteDiscount':
        return invalidLMRate(pkgId);

      default:
        assertNever(rule);
    }
  };

  const isSectionActive = (pkgId: number) => {
    const sectionInfo = activeRules.find((r) => r.pkgId === pkgId);

    return !!sectionInfo && sectionInfo.standardDiscount;
  };

  const isLMSectionActive = (pkgId: number) => {
    const sectionInfo = activeRules.find((r) => r.pkgId === pkgId);

    return !!sectionInfo && sectionInfo.lastMinuteDiscount;
  };

  const buildRuleTypeForDiscount = (
    pkg: ExtranetPackage,
    idx: number
  ): RuleType => ({
    key: 'standardDiscount',
    title: pkg.aliasForHotel,
    pkgId: pkg.id,
    fieldName: ['packageRules', idx],
  });

  const buildRuleTypeForLMDiscount = (
    pkg: ExtranetPackage,
    idx: number
  ): RuleType => ({
    key: 'lastMinuteDiscount',
    title: pkg.aliasForHotel,
    pkgId: pkg.id,
    fieldName: ['packageRules', idx],
  });

  const pricingRuleHDIW = [
    {
      title: 'Dynamic',
      description:
        'Your Staycation rate evolves with your BAR, according to the set discount rate.',
      emoji: '📈',
    },
    {
      title: 'Editable',
      description: isSingleRate
        ? 'You are still able to edit your rates manually on specific dates.'
        : 'You are still able to edit your rates on specific dates with the standard discount.',
      emoji: '🤚',
    },
    {
      title: 'Automatic',
      description:
        'Your connected stocks on all room categories will be automatically published.',
      emoji: '🤖',
    },
    {
      title: 'Global',
      description:
        'Your pricing rule will be applied to all of your room categories.',
      emoji: '🛌',
    },
    {
      title: 'Flexible',
      description:
        'You can deactivate the default discount at any time and revert to your initial rates and discount.',
      emoji: '🔄',
    },
  ];

  const hasPricingRuleInitially = !!packages.find(
    (pkg: ExtranetPackage) => !!pkg.autoDiscountRate
  );

  const hasLMPricingRuleInitially = !!packages.find(
    (pkg: ExtranetPackage) => !!pkg.lastMinuteAutoDiscountRate
  );

  return (
    <Modal
      centered
      width={610}
      className="automatedDiscountModal"
      open={isOpen}
      title={<PricingRuleModalHeader />}
      onCancel={handleCancelModal}
      destroyOnClose
      footer={
        <PricingRuleModalFooter
          handleCancelModal={handleCancelModal}
          handleDeactivationAutomation={handleDeactivationAutomation}
          isFormInvalid={!canSubmit}
          form={form}
          hasValue={hasPricingRuleInitially || hasLMPricingRuleInitially}
          openPricingRule={hasPricingRuleInitially}
          openLastMinutePricingRule={hasLMPricingRuleInitially}
          isSingleRate={isSingleRate}
          activeRules={activeRules}
          isUpdatingPricingRule={updatingPricingRule || deletingPricingRule}
        />
      }
    >
      <div className="automatedDiscountModal__modal_wrapper automatedDiscountModal__modal_wrapper__discount_form">
        <Form
          name="dynamicFormList"
          autoComplete="off"
          form={form}
          onFinish={handleSaveForm}
          initialValues={getInitialFormValues(packages)}
        >
          <div className="pricing-rule-section-header">
            <div className="pricing-rule-section-title">
              <div className="pricing-rule-section-icon">
                <ThunderboltFilled />
              </div>
              <p className="title">Standard Discount</p>
            </div>
          </div>
          {packages.map((pkg, index) => (
            <Fragment key={pkg.id}>
              <RuleSection
                ruleInfo={buildRuleTypeForDiscount(pkg, index)}
                isRuleActive={isSectionActive(pkg.id)}
                packages={packages}
                toggleSwitch={(switchValue: boolean) =>
                  toggleDefaultSwitch(switchValue, pkg.id)
                }
                isSingleRate={isSingleRate}
                isRuleInvalid={getIsRuleInvalid(pkg.id, 'standardDiscount')}
              />
            </Fragment>
          ))}
          <Divider className="pricing-rule-divider" plain>
            <div className="divider-text">and / or</div>
          </Divider>
          <div className="pricing-rule-section-header">
            <div className="pricing-rule-section-title">
              <div className="pricing-rule-section-icon">
                <DoubleThunderbolt />
              </div>
              <p className="title">Last Minute Discount</p>
            </div>
          </div>
          {packages.map((pkg, index) => (
            <Fragment key={pkg.id}>
              <RuleSection
                ruleInfo={buildRuleTypeForLMDiscount(pkg, index)}
                isRuleActive={isLMSectionActive(pkg.id)}
                packages={packages}
                toggleSwitch={(switchValue: boolean) =>
                  toggleLMSwitch(switchValue, pkg.id)
                }
                isSingleRate={isSingleRate}
                isRuleInvalid={getIsRuleInvalid(pkg.id, 'lastMinuteDiscount')}
              />
            </Fragment>
          ))}
        </Form>
      </div>
      <HowDoesItWork items={pricingRuleHDIW} trackFrom="pricing-rule" />
    </Modal>
  );
};
